import { Path, Editor, Text, Transforms, Node } from '../core';
import pipe from 'ramda/es/pipe';
import trim from 'ramda/es/trim';
import length from 'ramda/es/length';
import allPass from 'ramda/es/allPass';
import prop from 'ramda/es/prop';
import anyPass from 'ramda/es/anyPass';
import {
  capitalizeFirstWord,
  uncapitalizeFirstWord,
  chompLeft,
  chompRight,
  endsWithPunctuation,
  endsWithPunctuationOneOrMultipleWhitespace,
  endsWithEndOfSentencePunctuationOneOrMultipleWhitespace,
  startsWithPunctuation,
  isAcronym,
  isAnatomicalLocation,
  startsWith,
  startsWithContent,
  startsWithProperNoun,
  startsWithWhitespace,
  startsWithWhitespaceContent,
  endsWithContent,
  endsWithContentWhitespace,
  startsWithComma,
  endsWithComma,
  stripLeadingComma,
  appendWhitespaceIfNeeded,
  prependWhitespaceIfNeeded,
  startsWithWhitespacePunctuation,
  lengthOfStartingWhitespace,
  lengthOfEndingWhiteSpace,
  firstLetterOfFirstWord,
  endsWithUnitOfMeasure,
  startsWithUnitOfMeasure,
  startsWithWhitespaceUnitOfMeasure,
  removeUnitOfMeasureFromEndOfString,
  getUnitOfMeasureFromEndOfString,
  getUnitOfMeasureFromStartOfString,
  startsWithNumber,
  firstCharacterShouldHaveSpaceAfter,
  firstCharacterShouldHaveSpaceBefore,
  endsWithNumber,
  endsWithDecimal,
  endsWithWhitespace,
  startsWithUppercase,
  maybeUncapitalizeFirstLetterAfterTarget,
  endsWithNoCapitalizationAfterCharacter,
  endsWithSpacesAfterCharacter,
  containsEndOfSentencePunctuation,
  capitalizeWordsAfterPunctuation,
  endsWithPunctuationWhitespaceLeftParenthesis,
  endsWithTimeAbbreviation,
  NO_CAPITALIZATION_AFTER,
  shouldUncapitalizeNextWord,
} from './normalizationHelpers';
import { SECTION_HEADER_PLUGIN_ID } from '../plugins/sectionHeader';
import { getParentParagraph, isInlineParagraph, PARAGRAPH_PLUGIN_ID } from '../plugins';
import { HEADING_PLUGIN_ID } from '../plugins/heading';
import type { NodeEntry } from 'slate';
import { isSelectionInLineBreak } from '../plugins/lineBreak/utils';
import { logger } from 'modules/logger';
import { partialEditor } from '../utils/partialEditor';
import { CustomElement } from '../slate-custom-types';
import { Element } from 'slate';
import { autoCorrectStringText } from '../plugins/dictation/NvoqQueue/utils/autoCorrectNvoqStableText';
import type { ParagraphPluginElement } from '../plugins/paragraph/types';

export type NormalizeTextAffinity = 'left' | 'right' | 'both';

type TextNormalizationHandler = (
  editor: Editor,
  node: Text,
  arg3: {
    at: Path;
    affinity: NormalizeTextAffinity;
    respectParagraphBoundaries: boolean;
    preserveLeadingCase?: boolean;
  }
) => void;

const hasCommonTextBlockAncestor = (editor: Editor, path: Path, another: Path) => {
  const commonPath = Path.common(path, another);

  for (const [levelNode] of Array.from(Node.levels(editor, commonPath))) {
    if (
      (levelNode as CustomElement).type === PARAGRAPH_PLUGIN_ID ||
      (levelNode as CustomElement).type === HEADING_PLUGIN_ID
    ) {
      return true;
    }
  }

  return false;
};

const isInSectionHeader = (editor: Editor, textNodeBeforePath: Path) => {
  return (
    Array.from(
      Editor.levels(editor, {
        at: textNodeBeforePath,
        match: (n) => Element.isElement(n) && n.type === SECTION_HEADER_PLUGIN_ID,
      })
    ).length > 0
  );
};

const replaceFirstLetterOfNextWord = (
  editor: Editor,
  textNodeAfterPath: Path,
  newLetter: string,
  letterIndex: number
) => {
  // To avoid shifting the selection,
  // first add the new letter _after_ the old letter,
  const point = { path: textNodeAfterPath, offset: letterIndex + 1 };
  Transforms.insertText(editor, newLetter, { at: point });
  // then delete the old letter.
  Transforms.delete(editor, { at: { ...point, offset: letterIndex } });
};

const leftAffinityHandler: TextNormalizationHandler = (
  editor,
  node,
  { at, affinity, respectParagraphBoundaries, preserveLeadingCase }
) => {
  // Maybe filter by text nodes that contain content?
  let textEntryBefore: Nullable<NodeEntry<Text>> = Editor.previous(editor, {
    at,
    match: (n) => Text.isText(n) && Node.string(n).length > 0,
  });
  const textEntryBeforeWithContent = Editor.previous(editor, {
    at,
    match: (n) => Text.isText(n) && n.text.trim().length > 0,
  });
  let textEntryBeforeNoFilter = Editor.previous(editor, {
    at,
    match: (n) => Text.isText(n),
    mode: 'lowest',
  });

  if (
    respectParagraphBoundaries &&
    textEntryBefore != null &&
    !hasCommonTextBlockAncestor(editor, textEntryBefore[1], at)
  ) {
    textEntryBefore = null;
  }

  if (
    respectParagraphBoundaries &&
    textEntryBeforeWithContent != null &&
    !hasCommonTextBlockAncestor(editor, textEntryBeforeWithContent[1], at)
  ) {
    textEntryBefore = null;
  }

  // ensure inline paragraphs have a leading space if we are inserting at the start of an inline paragraph
  const maybeParentParagraph = getParentParagraph(editor, at);
  if (maybeParentParagraph != null && isInlineParagraph(maybeParentParagraph[0])) {
    const [parentNode, parentPath] = maybeParentParagraph as NodeEntry<ParagraphPluginElement>;
    const childIndex = at[parentPath.length];

    const doesParagraphHaveContentBefore =
      typeof childIndex === 'number' &&
      parentNode.children.slice(0, childIndex).some((child) => Node.string(child).length > 0);

    if (!doesParagraphHaveContentBefore) {
      node.text = prependWhitespaceIfNeeded(node.text);
    }
  }

  // At the start of the document
  if (!textEntryBefore) {
    if (!preserveLeadingCase) {
      node.text = capitalizeFirstWord(node.text);
    }
  } else {
    let isNodeBeforeLineBreak = false;

    const [textNodeBefore, textNodeBeforePath] = textEntryBefore;
    const isTextNodeBeforeInsideSectionHeader = isInSectionHeader(editor, textNodeBeforePath);
    try {
      if (textEntryBeforeNoFilter != null && Node.string(textEntryBeforeNoFilter[0]).length === 0) {
        isNodeBeforeLineBreak = isSelectionInLineBreak(
          editor,
          Editor.range(editor, textEntryBeforeNoFilter[1])
        );

        if (!isNodeBeforeLineBreak) {
          textEntryBeforeNoFilter = Editor.previous(editor, { at: textEntryBeforeNoFilter[1] });
          isNodeBeforeLineBreak =
            textEntryBeforeNoFilter != null
              ? isSelectionInLineBreak(editor, Editor.range(editor, textEntryBeforeNoFilter[1]))
              : false;
        }
      }
    } catch (e: any) {
      logger.warn(
        '[normalizeTextNodeAtPath] Error occurred when checking for line breaks for normalization',
        {
          error: e,
          node,
          at,
          editor: partialEditor(editor),
        }
      );
    }

    const [textNodeBeforeWithContent] = textEntryBeforeWithContent ?? [];
    if (
      textNodeBeforeWithContent &&
      !endsWithPunctuationWhitespaceLeftParenthesis(textNodeBeforeWithContent.text) &&
      anyPass([endsWithContent, endsWithContentWhitespace, endsWithNoCapitalizationAfterCharacter])(
        textNodeBeforeWithContent.text
      ) &&
      !isAcronym(chompLeft(node.text)) &&
      !isAnatomicalLocation(chompLeft(node.text).split(' ')[0]) &&
      !startsWithProperNoun(chompLeft(node.text)) &&
      anyPass([startsWithContent, startsWithWhitespaceContent])(node.text)
    ) {
      node.text = uncapitalizeFirstWord(node.text);
    }

    let isNodeBeforeHeading = false;

    // Heuristic of 3 or less words indicates before the first `:` to mark a heading.
    if (
      (chompRight(textNodeBefore.text).endsWith(':') || chompLeft(node.text).startsWith(':')) &&
      chompRight(textNodeBefore.text).split(' ').length <= 3
    ) {
      isNodeBeforeHeading = true;
      node.text = capitalizeFirstWord(node.text);
    }

    // for each punctuation in NO_CAPITALIZATION_AFTER, we want to maybe uncapitalize the word after it
    // except for the case of `:` where we only want to uncapitalize if the node before is not a heading
    NO_CAPITALIZATION_AFTER.split('').forEach((punctuation) => {
      if (isNodeBeforeHeading) {
        return;
      }

      node.text = maybeUncapitalizeFirstLetterAfterTarget(node.text, punctuation);
    });

    if (endsWithContentWhitespace(textNodeBefore.text) && startsWithWhitespace(node.text)) {
      node.text = chompLeft(node.text);
    }

    if (
      endsWithContentWhitespace(textNodeBefore.text) &&
      (anyPass([startsWithPunctuation, startsWithComma])(node.text) ||
        !firstCharacterShouldHaveSpaceBefore(node.text))
    ) {
      Transforms.delete(editor, {
        at: Editor.end(editor, textNodeBeforePath),
        distance: lengthOfEndingWhiteSpace(textNodeBefore.text),
        unit: 'character',
        reverse: true,
      });
    }

    if (
      startsWithContent(node.text) &&
      !endsWithTimeAbbreviation(textNodeBefore.text) &&
      (endsWithEndOfSentencePunctuationOneOrMultipleWhitespace(textNodeBefore.text) ||
        isTextNodeBeforeInsideSectionHeader ||
        isNodeBeforeLineBreak ||
        isNodeBeforeHeading)
    ) {
      // debugging edge cases for capitalization in the middle of text (CF-281)
      logger.info(
        `[normalizeTextNodeAtPath] Capitalizing first word after punctuation:
          endsWithEndOfSentencePunctuationOneOrMultipleWhitespace: ${endsWithEndOfSentencePunctuationOneOrMultipleWhitespace(textNodeBefore.text).toString()},
          isTextNodeBeforeInsideSectionHeader: ${isTextNodeBeforeInsideSectionHeader.toString()},
          isNodeBeforeLineBreak: ${isNodeBeforeLineBreak.toString()},
          isNodeBeforeHeading: ${isNodeBeforeHeading.toString()}`
      );
      node.text = capitalizeFirstWord(node.text);
    }

    const shouldAddWhitespaceAfterPunctuation =
      !isNodeBeforeLineBreak &&
      (endsWithPunctuation(textNodeBefore.text) || isTextNodeBeforeInsideSectionHeader) &&
      startsWithContent(node.text) &&
      (!endsWithDecimal(textNodeBefore.text) || !startsWithNumber(node.text)); // do not prepend a space if the node is the second half of a decimal

    if (shouldAddWhitespaceAfterPunctuation) {
      if (!endsWithTimeAbbreviation(textNodeBefore.text)) {
        // debugging edge cases for capitalization in the middle of text (CF-281)
        logger.info(
          `[normalizeTextNodeAtPath] Capitalizing first word and adding whitespace:
            endsWithPunctuation: ${endsWithPunctuation(textNodeBefore.text).toString()},
            isTextNodeBeforeInsideSectionHeader: ${isTextNodeBeforeInsideSectionHeader.toString()}
            endsWithDecimal: ${endsWithDecimal(textNodeBefore.text).toString()}
            startsWithNumber: ${startsWithNumber(node.text).toString()}`
        );
        node.text = capitalizeFirstWord(node.text);
      }

      node.text = prependWhitespaceIfNeeded(node.text);
    }

    if (endsWithComma(textNodeBefore.text) || endsWithSpacesAfterCharacter(textNodeBefore.text)) {
      // There are two scenarios here. The node before is either "," or ", " (or potentially more whitespace).
      // If it is the latter, we trim the previous node's whitespace and add a space to the start of the current node.
      if (!endsWithWhitespace(textNodeBefore.text)) {
        node.text = prependWhitespaceIfNeeded(node.text);
      } else if (endsWithWhitespace(textNodeBefore.text)) {
        node.text = prependWhitespaceIfNeeded(node.text);
        Transforms.delete(editor, {
          at: Editor.start(editor, textNodeBeforePath),
          unit: 'character',
          distance: textNodeBefore.text.length,
        });
        Transforms.insertText(editor, chompRight(textNodeBefore.text), {
          at: Editor.start(editor, textNodeBeforePath),
        });
      }
      if (
        !isNodeBeforeHeading &&
        !isAcronym(chompLeft(node.text)) &&
        !isAnatomicalLocation(chompLeft(node.text).split(' ')[0]) &&
        !startsWithProperNoun(chompLeft(node.text))
      ) {
        node.text = uncapitalizeFirstWord(node.text);
      }
    }

    if (
      !isNodeBeforeHeading &&
      startsWithComma(node.text) &&
      !isAcronym(stripLeadingComma(node.text)) &&
      !isAnatomicalLocation(stripLeadingComma(node.text)) &&
      !startsWithProperNoun(stripLeadingComma(node.text))
    ) {
      node.text = uncapitalizeFirstWord(node.text);
    }

    // If the textEntryBefore only contains whitespace sometimes we want to run special logic
    if (
      textEntryBeforeWithContent &&
      textEntryBefore &&
      !Path.equals(textEntryBeforeWithContent[1], textEntryBefore[1])
    ) {
      const [textNodeBeforeWithContent, textPathBeforeWithContent] = textEntryBeforeWithContent;
      const isTextNodeBeforeWithContentInsideSectionHeader = isInSectionHeader(
        editor,
        textPathBeforeWithContent
      );
      const textNodeBeforeWithContentEndsWithTimeAbbreviation = endsWithTimeAbbreviation(
        textNodeBeforeWithContent.text
      );

      if (
        (anyPass([endsWithPunctuationOneOrMultipleWhitespace, endsWithPunctuation])(
          textNodeBeforeWithContent.text
        ) ||
          isTextNodeBeforeWithContentInsideSectionHeader) &&
        startsWithContent(node.text) &&
        !textNodeBeforeWithContentEndsWithTimeAbbreviation
      ) {
        node.text = capitalizeFirstWord(node.text);
      }
    }

    // replace 'by' with 'x', if the context indicates a measurement
    if (
      (endsWithNumber(textNodeBefore.text) || endsWithDecimal(textNodeBefore.text)) &&
      startsWith('by ', { caseInsensitive: true })(node.text)
    ) {
      node.text = node.text.replace(/\bby\b/i, 'x');

      if (!endsWithWhitespace(textNodeBefore.text)) {
        node.text = prependWhitespaceIfNeeded(node.text);
      }
    }

    // This is inserting a space before the number
    if (
      !isNodeBeforeLineBreak &&
      firstCharacterShouldHaveSpaceBefore(node.text) &&
      endsWithContent(textNodeBefore.text) &&
      !startsWithComma(node.text) &&
      !startsWithPunctuation(node.text) &&
      (!startsWithNumber(node.text) || !endsWithNumber(textNodeBefore.text))
    ) {
      node.text = prependWhitespaceIfNeeded(node.text);
    }
  }
};

const rightAffinityHandler: TextNormalizationHandler = (
  editor,
  node,
  { at, affinity, respectParagraphBoundaries }
) => {
  const textEntryBefore = Editor.previous(editor, {
    at,
    match: (n) => Text.isText(n) && Node.string(n).length > 0,
  });

  let textEntryAfter: Nullable<NodeEntry<Text>> = Editor.next(editor, {
    at,
    match: (n) => Text.isText(n) && Node.string(n).length > 0,
  });

  let textEntryAfterWithContent: Nullable<NodeEntry<Text>> = Editor.next(editor, {
    at,
    match: (n) => Text.isText(n) && n.text.trim().length > 0,
  });

  if (
    respectParagraphBoundaries &&
    textEntryAfter &&
    !hasCommonTextBlockAncestor(editor, textEntryAfter[1], at)
  ) {
    textEntryAfter = null;
  }

  if (
    respectParagraphBoundaries &&
    textEntryAfterWithContent &&
    !hasCommonTextBlockAncestor(editor, textEntryAfterWithContent[1], at)
  ) {
    textEntryAfterWithContent = null;
  }

  // Capitalize the first letter of the next content-bearing text node when current node ends with punctuation.
  // Previously we only considered the immediate next text node, which fails when a whitespace-only node sits between.
  if (endsWithPunctuation(node.text) && textEntryAfterWithContent) {
    const [textNodeAfterWithContent, textNodeAfterWithContentPath] = textEntryAfterWithContent;
    const [letterToCapitalize, letterToCapitalizeIndex] = firstLetterOfFirstWord(
      textNodeAfterWithContent.text
    );
    if (letterToCapitalizeIndex > -1 && letterToCapitalize !== letterToCapitalize.toUpperCase()) {
      replaceFirstLetterOfNextWord(
        editor,
        textNodeAfterWithContentPath,
        letterToCapitalize.toUpperCase(),
        letterToCapitalizeIndex
      );
    }
  }

  if (textEntryAfter) {
    const [textNodeAfter, textNodeAfterPath] = textEntryAfter;

    if (
      anyPass([endsWithContent, endsWithPunctuation])(node.text) &&
      startsWithWhitespacePunctuation(textNodeAfter.text)
    ) {
      Transforms.delete(editor, {
        at: Editor.start(editor, textNodeAfterPath),
        unit: 'character',
        distance: lengthOfStartingWhitespace(textNodeAfter.text),
      });
    }

    const nextWordFollowsProperNounRules =
      textEntryBefore &&
      hasCommonTextBlockAncestor(editor, textEntryBefore[1], at) &&
      !endsWithPunctuation(chompRight(textEntryBefore[0].text)) &&
      startsWithUppercase(chompLeft(textNodeAfter.text));

    if (
      shouldUncapitalizeNextWord(node.text, textNodeAfter.text, {
        nextWordFollowsProperNounRules: Boolean(nextWordFollowsProperNounRules),
      })
    ) {
      const [letterToUncapitalize, letterToUncapitalizeIndex] = firstLetterOfFirstWord(
        textNodeAfter.text
      );
      if (
        letterToUncapitalizeIndex > -1 &&
        letterToUncapitalize !== letterToUncapitalize.toLowerCase()
      ) {
        replaceFirstLetterOfNextWord(
          editor,
          textNodeAfterPath,
          letterToUncapitalize.toLowerCase(),
          letterToUncapitalizeIndex
        );
      }
    }

    if (endsWithPunctuation(node.text)) {
      // Fetch latest text for the next node since earlier transforms may have mutated it
      const latestNextNode = Node.get(editor, textNodeAfterPath) as Text;
      if (startsWithPunctuation(latestNextNode.text)) {
        Transforms.delete(editor, {
          at: Editor.start(editor, textNodeAfterPath),
          unit: 'character',
          distance: 1,
        });
      }
    }

    if (endsWithUnitOfMeasure(chompRight(node.text))) {
      if (
        startsWithUnitOfMeasure(textNodeAfter.text) &&
        getUnitOfMeasureFromEndOfString(node.text) ===
          getUnitOfMeasureFromStartOfString(textNodeAfter.text)
      ) {
        node.text = chompRight(node.text);
        node.text = removeUnitOfMeasureFromEndOfString(node.text);
      }

      if (
        startsWithWhitespaceUnitOfMeasure(textNodeAfter.text) &&
        getUnitOfMeasureFromEndOfString(node.text) ===
          getUnitOfMeasureFromStartOfString(chompLeft(textNodeAfter.text))
      ) {
        node.text = pipe(chompRight, removeUnitOfMeasureFromEndOfString, chompRight)(node.text);
      }
    }

    /*
      This is unfortunately getting confusing, but the best way I could handle this for now
      This rule needs to only run if it is a right affinity, and theres no textNode before
      if there is a textNode before, it needs to let the bothAffinityHandler run below and handle appending whitespaces
      this is another good example of how we could refactor normalization with the rfc proposal.
    */
    if (
      node.text.trim().length > 0 &&
      startsWithContent(textNodeAfter.text) &&
      // if theres not a node before or after, or the affinity is not both, do this, otherwise use the bothAffinityHandler
      (!textEntryBefore || !textEntryAfter || affinity !== 'both') &&
      // do not do this if the node ends in a number and the node after starts with a number
      (!endsWithNumber(node.text) || !startsWithNumber(textNodeAfter.text))
    ) {
      node.text = appendWhitespaceIfNeeded(node.text);
    }

    // if the first character shouldnt have a space after, and there is a whitespace in the node after, remove it
    if (
      !firstCharacterShouldHaveSpaceAfter(node.text.trim()) &&
      lengthOfStartingWhitespace(textNodeAfter.text)
    ) {
      Transforms.delete(editor, {
        at: Editor.start(editor, textNodeAfterPath),
        unit: 'character',
        distance: lengthOfStartingWhitespace(textNodeAfter.text),
      });
    }
  }

  if (textEntryAfterWithContent) {
    const [textNodeAfterWithContent] = textEntryAfterWithContent;

    if (endsWithUnitOfMeasure(chompRight(node.text))) {
      if (
        startsWithUnitOfMeasure(textNodeAfterWithContent.text) &&
        getUnitOfMeasureFromEndOfString(node.text) ===
          getUnitOfMeasureFromStartOfString(textNodeAfterWithContent.text)
      ) {
        node.text = pipe(chompRight, removeUnitOfMeasureFromEndOfString, chompRight)(node.text);
      }

      if (
        startsWithWhitespaceUnitOfMeasure(textNodeAfterWithContent.text) &&
        getUnitOfMeasureFromEndOfString(node.text) ===
          getUnitOfMeasureFromStartOfString(chompLeft(textNodeAfterWithContent.text))
      ) {
        node.text = pipe(chompRight, removeUnitOfMeasureFromEndOfString, chompRight)(node.text);
      }
    }
  }
};

const bothAffinityHandler: TextNormalizationHandler = (
  editor,
  node,
  { at, respectParagraphBoundaries }
) => {
  const textEntryBefore = Editor.previous(editor, {
    at,
    match: (n) => Text.isText(n) && Node.string(n).length > 0,
  });

  const textEntryAfter: Nullable<NodeEntry<Text>> = Editor.next(editor, {
    at,
    match: (n) => Text.isText(n) && Node.string(n).length > 0,
  });

  let textEntryAfterWithContent = Editor.next(editor, {
    at,
    match: (n) => allPass([Text.isText, pipe(prop('text'), trim, length, Boolean)])(n),
  });

  // At the end of a paragraph, we don't want to append a space.
  if (
    respectParagraphBoundaries &&
    textEntryAfterWithContent != null &&
    !hasCommonTextBlockAncestor(editor, textEntryAfterWithContent[1], at)
  ) {
    textEntryAfterWithContent = undefined;
  }

  if (!textEntryAfter || !textEntryBefore) return;

  const [textNodeAfter, textNodeAfterPath] = textEntryAfter;
  const [textNodeBefore, textNodeBeforePath] = textEntryBefore;

  const nextWordFollowsProperNounRules =
    hasCommonTextBlockAncestor(editor, textNodeBeforePath, at) &&
    !endsWithPunctuation(chompRight(textNodeBefore.text)) &&
    startsWithUppercase(chompLeft(textNodeAfter.text));

  // It's important that the comma normalization happens before the whitespace normalization that proceeds it.
  if (
    (endsWithComma(node.text) || endsWithSpacesAfterCharacter(node.text)) &&
    (!isAcronym(chompLeft(textNodeAfter.text)) || !nextWordFollowsProperNounRules) &&
    at[0] === textNodeAfterPath[0] &&
    // Do not downcase if the next text starts with punctuation (e.g., ".")
    // because that punctuation likely terminates the sentence and the following
    // word should remain capitalized.
    !startsWithPunctuation(textNodeAfter.text) &&
    !startsWithWhitespacePunctuation(textNodeAfter.text)
  ) {
    Editor.withoutNormalizing(editor, () => {
      const [letterToUncapitalize, letterToUncapitalizeIndex] = firstLetterOfFirstWord(
        textNodeAfter.text
      );
      if (letterToUncapitalizeIndex > -1) {
        replaceFirstLetterOfNextWord(
          editor,
          textNodeAfterPath,
          letterToUncapitalize.toLowerCase(),
          letterToUncapitalizeIndex
        );
      }
    });
  }

  if (
    startsWithComma(node.text) &&
    !isAcronym(stripLeadingComma(node.text)) &&
    !isAnatomicalLocation(stripLeadingComma(node.text)) &&
    !startsWithProperNoun(stripLeadingComma(node.text))
  ) {
    node.text = uncapitalizeFirstWord(node.text);
  }

  if (
    textEntryAfterWithContent != null &&
    startsWithContent(textNodeAfter.text) &&
    firstCharacterShouldHaveSpaceAfter(node.text.trim()) &&
    // if the node before and after isnt numbers, ex : 10|20, add a space
    (!startsWithNumber(textNodeAfter.text) ||
      (!endsWithNumber(textNodeBefore?.text) && !endsWithNumber(node.text))) &&
    node.text.trim().length > 0
  ) {
    node.text = appendWhitespaceIfNeeded(node.text);
  }
};

const hasLeftAffinity = (affinity: NormalizeTextAffinity) => ['left', 'both'].includes(affinity);
const hasRightAffinity = (affinity: NormalizeTextAffinity) => ['right', 'both'].includes(affinity);
const hasBothAffinity = (affinity: NormalizeTextAffinity) => ['both'].includes(affinity);

/**
 * Normalizes a text node at a given path. Assumes the pass is already split!
 *
 * @see normalizeTextNodeAtPath
 */
export const normalizeTextNodeAtPath = (
  editor: Editor,
  textNodeToNormalize: Text,
  {
    at,
    affinity = 'both',
    respectParagraphBoundariesLeft = true,
    respectParagraphBoundariesRight = true,
    autoCorrectDictionary = {},
    preserveLeadingCase = false,
  }: {
    at: Path;
    affinity?: NormalizeTextAffinity;
    respectParagraphBoundariesLeft?: boolean;
    respectParagraphBoundariesRight?: boolean;
    autoCorrectDictionary?: Record<string, string>;
    preserveLeadingCase?: boolean;
  }
): Text => {
  const node: Text = { ...textNodeToNormalize };

  // If the text node is empty, there is nothing to normalize which has not already been normalized
  if (node.text === '') return node;

  // apply auto correct before other text normalizations.
  // E.g we still want auto correct to respect capitalization at beginning of sentences.
  node.text = autoCorrectStringText(node.text, autoCorrectDictionary);

  Editor.withoutNormalizing(editor, () => {
    if (hasLeftAffinity(affinity)) {
      leftAffinityHandler(editor, node, {
        at,
        affinity,
        respectParagraphBoundaries: respectParagraphBoundariesLeft,
        preserveLeadingCase,
      });
    }

    if (hasRightAffinity(affinity)) {
      rightAffinityHandler(editor, node, {
        at,
        affinity,
        respectParagraphBoundaries: respectParagraphBoundariesRight,
      });
    }

    if (hasBothAffinity(affinity)) {
      bothAffinityHandler(editor, node, {
        at,
        affinity,
        respectParagraphBoundaries: respectParagraphBoundariesRight,
      });
    }

    innerTextHandler(node);
  });

  return node;
};

const innerTextHandler = (node: Text) => {
  if (containsEndOfSentencePunctuation(node.text)) {
    node.text = capitalizeWordsAfterPunctuation(node.text);
  }
};
